import { CheckField, CmsField, CmsPolicy, insuranceCompany, RadioButtonField, TextSearchField } from '@/types/types';
import { formatter, getDate, isDateBefore } from './FormatService';
import store from '@/store/store';
import { isDevelop } from './environmentService';
import { INPUT_TYPES, POLICIES, POLICY_FIELDS, UNDERWRITING_TEXT_FIELDS } from '@/types/enums';
import { AcceptPage } from '@/cms/definitions/content-types';
import { getCmsPolicy } from './PageService';

export const doubleCurlyRegex = /{{(.+?)}}/gi;

export const customClickHandler = (stepId: string, fieldId: string, value) => {
	if (fieldId === POLICY_FIELDS.termination_option && value) {
		// KLK const coverStartDate = getCancellationDate(stepId);
		// KLK  perhaps set date
	}
};

export const getCancellationDate = (stepId: string, inputFields?: CmsField[]): Date => {
	if (!inputFields) {
		inputFields = store.state.ProposalContext.proposalSteps.get(stepId).inputFields;
	}

	const coverStartDate = inputFields.find((field) => field.id === POLICY_FIELDS.cover_start_date);
	const termination_option = inputFields.find(
		(field) => field.id === POLICY_FIELDS.termination_option
	) as RadioButtonField;

	if (!coverStartDate || !termination_option?.labels[0]) {
		return;
	}

	const coverStart = getDate(coverStartDate.value as string);
	const nearestCancellation = new Date();

	nearestCancellation.setMonth(nearestCancellation.getMonth() + 2);
	nearestCancellation.setDate(1);
	return coverStart && isDateBefore(nearestCancellation, coverStart) ? coverStart : nearestCancellation;
};

export const getReplacedValue = (value: string, replacements: Map<string, string>) => {
	const matches = value?.match?.(doubleCurlyRegex);
	if (matches?.length > 0) {
		return replacePlaceholder(value, matches, replacements);
	}
	return value;
};

const handleReplacements = (cmsField: CmsField, replacements: Map<string, string>) => {
	if (
		[
			POLICY_FIELDS.product_price_template.valueOf(),
			POLICY_FIELDS.product_affinity_template.valueOf(),
			POLICY_FIELDS.claims_insurance_template.valueOf(),
		].includes(cmsField.id)
	) {
		// early return;
		return;
	}
	if (cmsField.tooltip?.match(doubleCurlyRegex)) {
		cmsField.tooltip = getReplacedValue(cmsField.tooltip, replacements);
	}

	if (cmsField.value?.toString().match(doubleCurlyRegex)) {
		cmsField.value = getReplacedValue(cmsField.value as string, replacements);
	}
	if (cmsField.label?.match(doubleCurlyRegex)) {
		cmsField.label = getReplacedValue(cmsField.label, replacements);
	}
	if (cmsField.postLabel?.match(doubleCurlyRegex)) {
		cmsField.postLabel = getReplacedValue(cmsField.postLabel, replacements);
	}

	if (cmsField.placeholder?.match(doubleCurlyRegex)) {
		cmsField.placeholder = getReplacedValue(cmsField.placeholder, replacements);
	}
	handleLists(cmsField, replacements);
};

const handleLists = (cmsField: CmsField, replacements: Map<string, string>) => {
	if (cmsField.labels || cmsField.options) {
		const delimiter = '¤¤';
		let arr = cmsField.labels?.join(delimiter);

		if (arr?.match(doubleCurlyRegex)) {
			arr = getReplacedValue(arr, replacements);
			cmsField.labels = arr?.split(delimiter);
		}

		arr = cmsField.options?.join(delimiter);

		if (arr?.match(doubleCurlyRegex)) {
			arr = getReplacedValue(arr, replacements);
			cmsField.options = arr?.split(delimiter);
		}
	}
};

export const generalMapField = (
	cmsPolicy: CmsPolicy,
	cmsField,
	previousFieldId: string,
	inx: number,
	replacements: Map<string, string>
) => {
	cmsField.uiId = cmsField.id + inx; // unique id for html element

	if (cmsField.isDependentPrevious && previousFieldId) {
		cmsField.dependentId = previousFieldId;
	}

	if (cmsField.hidden) {
		cmsField.isValid = true;
		cmsField.readonly = true;
	}

	if (cmsField.labels || cmsField.options) {
		if (cmsField.labels) {
			cmsField.labels = cmsField.labels.split('\n').filter((elem) => elem !== '');
		}

		if (cmsField.options) {
			cmsField.options = cmsField.options.split('\n').filter((elem) => elem !== '');
		}

		if (cmsField.preferredLabels) {
			cmsField.preferredLabels = cmsField.preferredLabels.split('\n').filter((elem) => elem !== '');
		}

		cmsField.labels ??= [...cmsField.options];

		cmsField.options ??= [...cmsField.labels];

		if (cmsField.labels.length !== cmsField.options.length) {
			console.warn(
				'List labels and options differ in length -> id',
				cmsField.id,
				'Labels length;',
				cmsField.labels.length,
				'Options length:',
				cmsField.options.length
			);
		}

		handleSorting(cmsField);
	}

	mapSpecificFields(cmsPolicy, cmsField, replacements);
};

export const preProcessInsuranceCompanies = (cmsPolicy) => {
	if (!store.state.ProposalContext.insuranceCompanies) {
		return;
	}
	const cmsField: any = cmsPolicy.inputFields.find((field: any) => field.id === POLICY_FIELDS.previous_company_id);

	if (cmsField) {
		const insuranceCompanies = store.state.ProposalContext.insuranceCompanies;
		if (insuranceCompanies && insuranceCompanies?.length > 0) {
			cmsField.labels = '';
			cmsField.options = '';
			insuranceCompanies.forEach((company) => {
				if (isValidCompanyForProduct(cmsPolicy.type, company)) {
					cmsField.labels += company.company_name + '\n';
					cmsField.options += company.company_id + '\n';
				}
			});
		}
	}
};

const isValidCompanyForProduct = (productType, company: insuranceCompany) => {
	const typeUpper = productType.toLocaleUpperCase();
	switch (typeUpper) {
		case POLICIES.CAR:
			return company.is_auto_insurer;
		case POLICIES.ACCIDENT:
			return company.is_accident_insurer;
		default:
			return true;
	}
};

const mapSpecificFields = (cmsPolicy, cmsField: CmsField, replacements: Map<string, string>) => {
	switch (cmsField.contentType) {
		case INPUT_TYPES.CheckField:
			cmsField.isValid = !cmsField.isRequired;
			if (cmsField.masterId) {
				cmsField.class = cmsField.class ? cmsField.class + ' checkbox-child' : 'checkbox-child';
				cmsPolicy.hasMasterChildCheckFields = true;
			}
			// set default false alias value if exist and field has no value
			if (!cmsField.value && (cmsField as CheckField).checkedValue && (cmsField as CheckField).unCheckedValue) {
				cmsField.value = (cmsField as CheckField).unCheckedValue;
			}
			break;
		case INPUT_TYPES.TextOnlyField:
			// replace Optimizely inserted <p> with <span>
			cmsField.isValid = true;
			if (cmsField.value) {
				cmsField.value = replaceHtmlParagraph(cmsField.value as string);
			}
			break;
		case INPUT_TYPES.NumberField:
			cmsField.contentType = INPUT_TYPES.TextField;
			cmsField.type = 'number';
			break;
		default:
			break;
	}

	if (cmsField.tooltip?.includes('<p>')) {
		cmsField.tooltip = replaceHtmlParagraph(cmsField.tooltip);
	}
	handleReplacements(cmsField, replacements);
};

const replaceHtmlParagraph = (htmlValue: string) => {
	return htmlValue.replaceAll('<p>', '<span>').replaceAll('</p>', '</span>');
};

export const preProcessCheckFields = (inputFields: CmsField[]) => {
	const groupedFields = inputFields.filter(
		(cmsField) =>
			cmsField.contentType === INPUT_TYPES.CheckField && !cmsField.masterId && cmsField.isGroupedWithNoMasterField
	);
	if (groupedFields?.length < 1) {
		return;
	}

	groupedFields.forEach((cmsField, inx) => {
		if (cmsField.label) {
			cmsField.class = cmsField.class ? cmsField.class + ' checkbox-group first' : 'checkbox-group first';
			if (inx > 0) {
				groupedFields[inx - 1].class += ' last';
			}
			return;
		}

		cmsField.class = cmsField.class ? cmsField.class + ' checkbox-group member' : 'checkbox-group member';
	});
	groupedFields[groupedFields.length - 1].class += ' last';
};

export const preProcessFieldsByCustomerType = (inputFields: CmsField[]) => {
	if (store.state.ProposalContext.party.customer_type === 'SECONDARY') {
		// lock all fields and "remove" fields (showIfApiValue), that don't have an API value
		inputFields.forEach((field) => {
			field.readonly = true;
			field.isValid = true;
			field.showIfApiValue = true;
			if (field.hideIfApiValue) {
				field.showIfApiValue = false;
				field.hidden = true;
				field.readonly = false; // sanity
				field.isValid = true; // sanity
			}
		});
	}
};

export const handleSorting = (cmsField: TextSearchField) => {
	if (cmsField.contentType === INPUT_TYPES.TextSearchField) {
		if (cmsField.sortLabels) {
			const sortedLabels = [...cmsField.labels];

			sortedLabels.sort((a, b) => a.localeCompare(b));
			const labels = [];
			const options = [];
			sortedLabels.forEach((elem) => {
				labels.push(elem);
				const inx = cmsField.labels.indexOf(elem);
				options.push(cmsField.options[inx]);
			});
			cmsField.labels = labels;
			cmsField.options = options;
		}

		if (cmsField.preferredLabels && cmsField.preferredLabels.length > 0) {
			const removedLabels = [];
			const removedOptions = [];
			cmsField.preferredLabels.forEach((label) => {
				const inx = cmsField.labels.indexOf(label);
				if (inx > -1) {
					removedLabels.push(cmsField.labels.splice(inx, 1)[0]);
					removedOptions.push(cmsField.options.splice(inx, 1)[0]);
				}
			});
			cmsField.labels = [...removedLabels, ...cmsField.labels];
			cmsField.options = [...removedOptions, ...cmsField.options];
		}
	}
};

const replacePlaceholder = (value: string, matches: string[], replacements: Map<string, string>) => {
	let result = value;
	let regMatch: RegExp;
	let replacement: string;

	matches.forEach((match) => {
		if (match.toLowerCase() === '{{tooltip}}') {
			return;
		}
		regMatch = new RegExp(match, 'gi');
		replacement = replacements.get(match) ?? '';

		if (isDevelop && replacement === '') {
			replacement = `!NA${match}`;
		}
		result = result.replace(regMatch, replacement).trim();
	});
	return result;
};

export const mapProductHighlightField = (step, mapReceipt = false) => {
	const policies = store.state.ProposalContext.originalRequest.policies;
	const productHighlights = [];
	const affinityFields: { title: string; value: string }[] = [];
	const policyPriceTemplate = step.inputFields.find((field) => field.id === POLICY_FIELDS.product_price_template);

	let hasAffinity = false;
	// remove previously dynamic generated fields
	step.inputFields = step.inputFields.filter((field) => !field.dynamicField);

	policies.forEach((policy, inx) => {
		if (!policy.description) {
			console.warn('Product Field has no valid policy.description');
		}
		let value = '';
		if (inx === 0) {
			value += '<div class="divider divider__margin__bottom"></div>';
		}

		const policyPrice = { ...policyPriceTemplate };

		const frequency = policy[POLICY_FIELDS.payment_frequency];

		let paymentFrequencyText = 'årlig';
		let priceFrequencyText = 'år';
		switch (policy[POLICY_FIELDS.payment_frequency]) {
			case 2:
				paymentFrequencyText = 'halvårlig';
				priceFrequencyText = '6md.';
				break;
			case 4:
				paymentFrequencyText = 'kvartalsvis';
				priceFrequencyText = '3md.';
				break;
			case 12:
				paymentFrequencyText = 'månedlig';
				priceFrequencyText = 'md.';
				break;
		}

		let paymentMethod = policy[POLICY_FIELDS.payment_method];
		if (!mapReceipt) {
			paymentMethod = store.state.ProposalContext.proposalSteps
				.get(policy.id)
				?.inputFields.find((field) => field.id === 'payment_method')?.value;
		}

		const paymentType = paymentMethod === 'PBS' ? 'BS-aftale' : 'Girokort';
		const paymentInfo = `${paymentType} (${paymentFrequencyText} betaling)`;
		const price = `${formatter.format(policy.price / frequency)} kr./${priceFrequencyText}`;

		value += policyPrice.value.replaceAll(/{{SYSTEM_POLICY_TITLE}}/gi, policy.title ?? policy.type ?? '');
		value = value.replaceAll(/{{SYSTEM_POLICY_DESCRIPTION}}/gi, policy.description ?? '');
		value = value.replaceAll(/{{SYSTEM_POLICY_PAYMENT}}/gi, paymentInfo);
		value = value.replaceAll(/{{SYSTEM_POLICY_PRICE}}/gi, price);

		if (inx === policies.length - 1) {
			value += '<div class="divider divider__margin__top"></div>';
		}
		policyPrice.value = value;
		policyPrice.id = policy.title + inx;
		policyPrice.uiId = policy.title + inx;
		policyPrice.isVisible = true;
		policyPrice.isValid = true;
		policyPrice.hidden = false;
		policyPrice.dynamicField = true;

		productHighlights.push(policyPrice);

		// only one non matching Affinities for now
		if (policy.affinity) {
			hasAffinity = true;
			if (!affinityFields.some((field) => field.title === policy.affinity)) {
				affinityFields.push({ title: policy.affinity, value: policy.affinityValue });
			}
		}
	});

	if (hasAffinity) {
		const header = getAffinityHeader(step);
		if (header) {
			const affinityField = getAffinityField(step, affinityFields);
			if (affinityField) {
				productHighlights.push(header, affinityField);
			}
		}
	}
	// move highlights to front
	step.inputFields = [...productHighlights, ...step.inputFields];

	// DEBUG const fees = step.inputFields.find((field) => field.id === POLICY_FIELDS.total_fees);
	// DEBUG if (fees) {
	// DEBUG 	fees.isVisible = true;
	// DEBUG 	fees.isValid = true;
	// DEBUG 	fees.hidden = false;
	// DEBUG }
};

const getAffinityHeader = (step) => {
	const policyAffinityHeaderTemplate = step.inputFields.find(
		(field) => field.id === POLICY_FIELDS.product_affinity_header_template
	);
	let policyAffinityHeader;
	if (policyAffinityHeaderTemplate) {
		policyAffinityHeader = { ...policyAffinityHeaderTemplate };
		policyAffinityHeader.id = policyAffinityHeaderTemplate.id + '_dynamic';
		policyAffinityHeader.uiId = policyAffinityHeaderTemplate.uiId + '_dynamic';
		policyAffinityHeader.isVisible = true;
		policyAffinityHeader.isValid = true;
		policyAffinityHeader.hidden = false;
		policyAffinityHeader.dynamicField = true;
	}
	return policyAffinityHeader;
};

const getAffinityField = (step, affinityFields: { title: string; value: string }[]) => {
	const policyAffinityTemplate = step.inputFields.find(
		(field) => field.id === POLICY_FIELDS.product_affinity_template
	);

	let policyAffinity;
	if (policyAffinityTemplate) {
		policyAffinity = { ...policyAffinityTemplate };
		policyAffinity.id = policyAffinityTemplate.id + '_dynamic';
		policyAffinity.uiId = policyAffinityTemplate.uiId + '_dynamic';
		policyAffinity.isVisible = true;
		policyAffinity.isValid = true;
		policyAffinity.hidden = false;
		policyAffinity.dynamicField = true;

		const value = policyAffinity.value as string;

		policyAffinity.value = '';

		const replacements = new Map<string, string>();

		for (const affinityField of affinityFields) {
			replacements.set('{{SYSTEM_AFFINITY}}', affinityField.title);
			replacements.set('{{SYSTEM_AFFINITY_AMOUNT}}', affinityField.value || 'Rabat inkluderet');
			policyAffinity.value += value;
			handleReplacements(policyAffinity, replacements);
		}

		// Add end line divider
		policyAffinity.value += '<div class="divider divider__margin__bottom">&nbsp;</div>';
	}
	return policyAffinity;
};
export const healthCheck = (policy, cmsPolicy) => {
	console.log('HEALTH_CHECK - DEVELOP');
	const onlyCmsFields = [
		POLICY_FIELDS.cancelExistingPolicy,
		POLICY_FIELDS.termination_option,
		POLICY_FIELDS.cancelExistingPolicyText,
	];
	let match = undefined;
	console.log('Testing CMS fields ******************************');

	cmsPolicy.inputFields.forEach((cmsField: CmsField) => {
		match = policy.policy_object.hasOwnProperty(cmsField.id);
		if (!match) {
			match = onlyCmsFields.find((fieldId) => fieldId === cmsField.id);
			if (!match) {
				console.log(
					'Cms field not in product',
					cmsField.id,
					cmsField,
					'policy',
					policy.policy_object[cmsField.id]
				);
			}
		}
	});
	console.log('Testing Product fields ******************************');

	for (const property in policy.policy_object) {
		const match = cmsPolicy.inputFields.find((cmsField) => cmsField.id === property);
		if (!match) {
			console.log('Product field not in cms', property);
		}
	}

	console.log('END HEALTH_CHECK - DEVELOP ******************************');
};

/**
 *
 * @param policy Puts fields in the policy.policy_object Object, that are placed on lower levels
 * This goes for the field cover_start_date, the policy.termination fields and policy.payment
 * @returns void
 */
export const flattenProductFields = (policy) => {
	policy.policy_object ??= {};
	if (policy.termination) {
		const propertyNames = Object.keys(policy.termination);
		propertyNames.forEach((name) => {
			policy.policy_object[name] = policy.termination[name];
		});
	}
	policy.policy_object[POLICY_FIELDS.payment_method] = policy[POLICY_FIELDS.payment_method];
	policy.policy_object[POLICY_FIELDS.payment_frequency] = policy[POLICY_FIELDS.payment_frequency];
	policy.policy_object[POLICY_FIELDS.reg_no] = policy[POLICY_FIELDS.reg_no];
	policy.policy_object[POLICY_FIELDS.account] = policy[POLICY_FIELDS.account];

	// DEBUG if (policy.payment) {
	// 	DEBUG const propertyNames = Object.keys(policy.payment);
	// 	DEBUG propertyNames.forEach((name) => {
	// 	DEBUG 	policy.policy_object[name] = policy.payment[name];
	// 	DEBUG });
	// DEBUG}

	//cover_start_date mapped to field
	policy.policy_object.cover_start_date = policy.cover_start_date;
};

export const mapProductClaims = (step, cmsModel: AcceptPage) => {
	const policies = store.state.ProposalContext.originalRequest.policies;
	const productClaims = [];
	const claimsForProduct = step.inputFields.find((field) => field.id === POLICY_FIELDS.claims_insurance_template);
	const claimsForProductInx = step.inputFields.indexOf(claimsForProduct);

	let hasCLaimForAnyProduct = false;
	// remove previously dynamic generated fields
	step.inputFields = step.inputFields.filter((field) => !field.dynamicField);
	policies.forEach((policy, inx) => {
		const cmsPolicy = getCmsPolicy(cmsModel, policy.type);
		let policyTitleFromProperties: string;
		for (const prop of cmsPolicy?.content?.productProperties || []) {
			if (prop.key.toLowerCase() === 'full_policy_name') {
				policyTitleFromProperties = prop.value;
				break;
			}
		}

		if (policy.total_claims_for_this_insurance_type !== undefined) {
			hasCLaimForAnyProduct = true;
			const claimsTemplate = { ...claimsForProduct };

			claimsTemplate.label = claimsForProduct.label.replaceAll(
				/{{FULL_POLICY_NAME}}/gi,
				policyTitleFromProperties ?? policy.title ?? policy.type ?? ''
			);
			claimsTemplate.value = policy.total_claims_for_this_insurance_type + '';

			claimsTemplate.id = policy.title + inx;
			claimsTemplate.uiId = policy.title + inx;
			claimsTemplate.isVisible = true;
			claimsTemplate.isValid = true;
			claimsTemplate.hidden = false;
			claimsTemplate.dynamicField = true;

			productClaims.push(claimsTemplate);
		}
	});

	if (!hasCLaimForAnyProduct) {
		// no claims values on any product - hide the claims text field
		const claimsTextField = step.inputFields.find((field) => field.id === UNDERWRITING_TEXT_FIELDS.claims_text);
		if (claimsTextField) {
			claimsTextField.hidden = true;
		}
	}

	// move claims to correct position in fields array to front
	step.inputFields.splice(claimsForProductInx, 0, ...productClaims);
};

const hasValue = (value): boolean => {
	if (
		value === undefined ||
		value === null ||
		value === 'null' ||
		value === 'undefined' ||
		(value + '').trim() === ''
	) {
		return false;
	}
	return true;
};

export const mapApiToModel = (cmsField: CmsField, apiFieldValue) => {
	const hasValue_ = hasValue(apiFieldValue);
	cmsField.isValid = hasValue_;

	if (hasValue_) {
		cmsField.value = apiFieldValue + '';
		cmsField.initialValue = apiFieldValue + '';
	}

	if (cmsField.options && cmsField.value) {
		// test that value is on options
		if (!cmsField.options.includes(cmsField.value + '')) {
			cmsField.value = undefined;
			cmsField.readonly = false;
			cmsField.isValid = false;
			cmsField.hidden = false;
			cmsField.showIfApiValue = false;
		}
	}

	if (cmsField.hideIfApiValue && cmsField.value) {
		cmsField.hidden = true;
		cmsField.showIfApiValue = false; // sanity
		cmsField.readonly = false; // sanity
		cmsField.isValid = true; // sanity
	}
	if (cmsField.readonly && !hasValue_) {
		// sanity check
		// if readonly and no value
		cmsField.readonly = false;
		cmsField.isValid = false;
	}
};

export const moveReadonlyFieldsToTop = (cmsBlock) => {
	// sort so readonly fields comes first.

	const fieldsAtTop = [];
	const fieldsAtBottom = [];

	const addedFieldsIds: string[] = [];

	cmsBlock.inputFields.forEach((field: CmsField) => {
		// field has to be readonly and not already present
		// a field can be multiple present if it is dependent on a specific product variation
		if (field.readonly && !field.hidden && !addedFieldsIds.includes(field.id)) {
			if (field.dependentId !== POLICY_FIELDS.insurance_type) {
				// only remove dependency if field is not dependent on insurance_type
				field.dependentId = undefined;
			}
			fieldsAtTop.push(field);
			addedFieldsIds.push(field.id);
		} else {
			fieldsAtBottom.push(field);
		}
	});
	cmsBlock.inputFields = [...fieldsAtTop, ...fieldsAtBottom];
};
